import { z } from 'zod'
import {
  STYLE_SOURCES,
  articleHtmlSchema,
  articleLinesSchema,
  articleSortSchema,
  defaultSourceHeadings,
  presentationBySourceSchema,
  sectionStylesSchema,
  sourceHeadingsSchema,
  storedSnippetModeSchema,
  stylesBySourceSchema,
  type SourceHeadings,
  type StyleSource,
} from './template-article'
import {
  docTabsSchema,
  templateMastheadSchema,
  templateShellSchema,
  templateTargetSchema,
} from './template-layout'
import { FORBIDDEN_HTML, hexColorSchema, richTextContentSchema } from './template-primitives'

/**
 * Report templates (CLI-643) — the block document model.
 *
 * A template is a JSON document: a theme plus an ordered list of typed blocks. It dictates how a
 * report RENDERS, never which content is in it — queries, sources and limits belong to the
 * report's section config. One document is projected per delivery target (email today; Google
 * Doc, Sheet and CSV are later phases), which is the reason this is structured data rather than
 * an HTML string: HTML can feed an email, but a Sheet has nothing to project from it, and the
 * legacy Google Doc pipeline's convert-then-restyle-by-pattern-matching is what "the markup lost
 * its meaning" looks like in production.
 *
 * Rich-text block content is stored as a bounded HTML string (the TipTap editor's output) with
 * merge fields as `<span data-merge-field="…">` atoms — pills in the editor, resolved by
 * `@headline/report-render` at render time. Plain-text fields (the masthead) carry `{{token}}`
 * syntax instead, resolved by the same renderer.
 */

// The document's layers live in sibling modules (CLI-876); this module stays the one import path.
export { MERGE_FIELDS, mergeFieldLabel, type MergeFieldToken } from './template-primitives'
export * from './template-layout'
export * from './template-article'

export const sectionPresentationSchema = z.object({
  preset: z.enum(['headlines', 'standard', 'detailed']),
  fields: z.object({
    outlet: z.boolean(),
    date: z.boolean(),
    author: z.boolean(),
    summary: z.boolean(),
    /**
     * The "Read more" link to the licensed full text — present in 48 of the 56 legacy templates,
     * and only rendered when the article actually carries a full-text URL.
     */
    readMore: z.boolean().default(false),
  }),
  snippets: z.object({
    mode: storedSnippetModeSchema,
    /** Max snippets per article, when mode isn't 'none'. */
    limit: z.number().int().min(1).max(10),
    highlightTerms: z.boolean(),
    /**
     * How they sit under the article. 'bullets' is a disc list, which is what 47 of the 56 legacy
     * templates use; 'block' is the quoted style this renderer started with. Defaulted to 'block'
     * so nothing already built moves.
     */
    style: z.enum(['block', 'bullets']).default('block'),
    /**
     * The highlight's background, as `#rrggbb`. Unset leaves the bare `<mark>` (the browser's
     * yellow). Legacy sent every matched term on light blue and a principal's name on yellow —
     * the data API's `not-principal` / `principal` spans — so the standard template names both
     * (`LEGACY_HIGHLIGHT_COLOR`, `LEGACY_PRINCIPAL_HIGHLIGHT_COLOR`) and a migrated report
     * highlights as its legacy send did. The principal colour only applies with a term colour.
     */
    highlightColor: hexColorSchema.optional(),
    principalHighlightColor: hexColorSchema.optional(),
  }),
  syndication: z.object({
    show: z.boolean(),
    limit: z.number().int().min(1).max(50),
    sort: z.enum(['newest', 'alpha']),
  }),
  markers: z.object({
    opinion: z.boolean(),
    paywall: z.boolean(),
  }),
  /**
   * Where the outlet appears. `meta` puts it on the date-and-author line beneath the headline;
   * `beforeHeadline` bolds it inline ahead of the headline, which is how the legacy templates read
   * — "**The Example Times** Acme names a new chief executive" — and is not reachable by styling,
   * because it is composition rather than appearance.
   *
   * Only the outlet moves. Date and author stay on their own line either way, which is what the
   * originals do and what keeps the headline row short enough to scan.
   */
  outletPlacement: z.enum(['meta', 'beforeHeadline']).default('meta'),
  sort: articleSortSchema,
  /**
   * Whether articles stack as blocks or sit in a disc list.
   *
   * `bullets` is the `utah-template` shape — `<ul class="article-list">` with each article an
   * `<li>` — and it is the largest non-default template in use. Composition rather than
   * appearance, like `outletPlacement`, which is why no amount of styling reaches it. Defaulted
   * to `blocks` so nothing already built moves.
   */
  articleLayout: z.enum(['blocks', 'bullets']).default('blocks'),
  /**
   * Per-element colour, size and weight. Defaulted rather than required so every presentation
   * stored before this existed keeps parsing and keeps rendering identically — an empty object
   * means "the renderer's defaults", which is exactly what those documents already got.
   */
  styles: sectionStylesSchema.default({}),
  /**
   * Hand-written article markup, per source. An entry replaces the built-in layout for articles
   * from that source; the formatting controls stop applying to them, because the markup is the
   * layout. Empty is the common case and the default.
   */
  articleHtml: articleHtmlSchema.default({}),
  /** Empty means the renderer's built-in arrangement — see `articleLinesSchema`. */
  lines: articleLinesSchema.default([]),
  /**
   * Per-source deviations from everything above. Empty is the common case, and the default, so a
   * header that treats every source alike says nothing here.
   */
  bySource: presentationBySourceSchema.default({}),
  /**
   * The section's own heading — its name, and the accent rule beneath it.
   *
   * Both on by default, because that is what every template has had. Off is for a design that
   * draws its own section bars in a custom section: without this, a fully custom email still got
   * the renderer's heading above the one it drew itself, which is the sort of thing that makes an
   * escape hatch not quite an escape.
   */
  header: z
    .object({ show: z.boolean().default(true), rule: z.boolean().default(true) })
    .default({ show: true, rule: true }),
  /**
   * Whether a header that produced no stories today appears at all — its heading with nothing
   * under it. Off by default, because that is what legacy did: the default template wrapped every
   * header in `{{#notEmptyHeader}}` (52 of 57 custom templates too), so a quiet day's header
   * simply was not there. On is for a client who wants to see "Principal Mentions" every day even
   * when it is empty. Per header as well as per template, like everything else here.
   */
  showEmptySections: z.boolean().default(false),
  /**
   * Headings between the source groups inside a header. Off by default, so every template written
   * before this renders exactly as it did; see `sourceHeadingsSchema` for what turning it on does.
   */
  sourceHeadings: sourceHeadingsSchema.default(defaultSourceHeadings()),
  /**
   * Deviations for articles from one source, layered over `styles`. Empty is the common case and
   * the default, so a header that treats everything alike says nothing here.
   */
  stylesBySource: stylesBySourceSchema.default({}),
})
export type SectionPresentation = z.infer<typeof sectionPresentationSchema>

/**
 * What a header calls one of its sources — the label the `sourceHeadings` setting gives it, else
 * the product's own name for the source. Also the value of the `{{source_label}}` article token.
 */
export function sourceHeadingLabel(
  headings: SourceHeadings,
  source: StyleSource | undefined,
): string {
  const key = source ?? 'news'
  return headings.labels[key] ?? STYLE_SOURCES.find((s) => s.key === key)?.label ?? key
}

/**
 * Every block carries these. `htmlOverride` is the escape valve carried over from the prototype's
 * scrapped block editor: when set, the email emitter renders exactly this HTML for the block and
 * the structured fields are suspended. Email target only — a hand-written override has nothing to
 * say to a Sheet. In the contract from day one so the emitter and editor can meet at it; the
 * editor UI for it is a later phase.
 */
const blockBase = z.object({
  id: z.string().min(1).max(64),
  enabled: z.boolean(),
  htmlOverride: richTextContentSchema.optional(),
})

export const greetingBlockSchema = blockBase.extend({
  type: z.literal('greeting'),
  content: richTextContentSchema,
})

export const richTextBlockSchema = blockBase.extend({
  type: z.literal('richText'),
  content: richTextContentSchema,
})

/**
 * The load-bearing block: renders the report's headers. `default` is the house style — how an
 * article looks in this template, for any report using it.
 *
 * It deliberately holds no per-header settings. It used to (`perHeader`, keyed by header id), and
 * that was wrong once templates became shareable: header ids belong to one report, so a library
 * template carried one account's ids into every other account that used it, where they matched
 * nothing and every header silently fell back to the default. Nothing ever wrote the map, so
 * removing it migrates no data — zod drops the leftover key on read.
 *
 * A header that wants its own presentation now says so on itself: `presentation` on the report's
 * section, which is where a report-specific fact belongs. See `reportSectionSchema`.
 */
export const sectionsBlockSchema = blockBase.extend({
  type: z.literal('sections'),
  default: sectionPresentationSchema,
})

export const signatureBlockSchema = blockBase.extend({
  type: z.literal('signature'),
  /**
   * 'custom' is the only source until sender identities exist (a later phase adds
   * 'senderIdentity', pointing the block at the report's sender instead of inline content).
   */
  source: z.literal('custom'),
  /** The complimentary close — "Best,". Its own field so a signature swap keeps the closing. */
  closing: z.string().max(100),
  content: richTextContentSchema,
})

/**
 * Optional. A contents list over the report's sections — the setting that moved out of report
 * config's Formatting tab, where it had no way to say WHERE the list goes. Here, placement is
 * the block's position in `blocks`, so there is no placement field to disagree with it.
 */
export const tocBlockSchema = blockBase.extend({
  type: z.literal('toc'),
  /** Ordered list (1. 2. 3.) rather than bulleted. */
  numbered: z.boolean(),
  /** Nest each section's headlines under its name, instead of listing section names alone. */
  groupBySection: z.boolean(),
  /** Omit sections that produced no articles today, rather than listing a name with nothing. */
  skipEmpty: z.boolean(),
})

/**
 * Optional. Renders insights the report already generated — WHICH insights exist, and from what,
 * stays in report config. Only their position and treatment are template decisions, which is the
 * split agreed for the Insights tab.
 */
export const insightsBlockSchema = blockBase.extend({
  type: z.literal('insights'),
  heading: z.string().max(100),
  /** 'accent' draws the block against the account's brand colour; 'plain' is a neutral rule. */
  style: z.enum(['accent', 'plain']),
  showCitations: z.boolean(),
})

/**
 * Optional, and the only block a template may hold more than one of.
 *
 * Every other block answers a question the document asks once — there is one greeting, one
 * contents list. This one exists because customers keep needing a part we did not anticipate: an
 * editor's note, an events list, a legal footer. Rather than growing a block type per request,
 * this is a free-form region with a heading, a treatment, and rich text, and the template author
 * decides what it means.
 *
 * `heading` is optional in practice — empty renders the content with no title, which is what a
 * disclaimer wants.
 */
export const customSectionBlockSchema = blockBase.extend({
  type: z.literal('customSection'),
  heading: z.string().max(100),
  /** Matches the insights vocabulary, plus a neutral box: brand-tinted, boxed, or bare. */
  style: z.enum(['plain', 'accent', 'bordered']),
  content: richTextContentSchema,
})

export const templateBlockSchema = z.discriminatedUnion('type', [
  greetingBlockSchema,
  richTextBlockSchema,
  sectionsBlockSchema,
  signatureBlockSchema,
  tocBlockSchema,
  insightsBlockSchema,
  customSectionBlockSchema,
])
export type TocBlock = z.infer<typeof tocBlockSchema>
export type CustomSectionBlock = z.infer<typeof customSectionBlockSchema>
export type InsightsBlock = z.infer<typeof insightsBlockSchema>
export type TemplateBlock = z.infer<typeof templateBlockSchema>
export type TemplateBlockType = TemplateBlock['type']

/**
 * The block document. `version` is the migration seam: a stored document declares the shape it
 * was written in, so a later shape change is a translation step instead of a guess. `kind` is the
 * other seam (CLI-840): absent or `blocks` is this shape; `html` is the HTML-first document below.
 */
export const blockTemplateDocumentSchema = z
  .object({
    version: z.literal(2),
    /** Absent on every document written before the HTML kind existed, so it stays optional. */
    kind: z.literal('blocks').optional(),
    masthead: templateMastheadSchema,
    /** Optional so every document written before the shell existed keeps rendering identically. */
    shell: templateShellSchema.optional(),
    /** Absent means email — every template written before there was a choice. */
    target: templateTargetSchema.default('email'),
    /** Only meaningful when `target` is `googleDoc`; ignored, not rejected, otherwise. */
    docTabs: docTabsSchema.optional(),
    blocks: z.array(templateBlockSchema).min(1).max(20),
  })
  /**
   * Block ids must be unique. Nothing enforced this while every block was a singleton with a
   * hardcoded id, but custom sections repeat, and the editor addresses blocks by id to reorder,
   * toggle and edit them — two blocks sharing one id means editing either edits both.
   */
  .superRefine((doc, ctx) => {
    const seen = new Set<string>()
    for (const [i, block] of doc.blocks.entries()) {
      if (seen.has(block.id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['blocks', i, 'id'],
          message: `Duplicate block id "${block.id}".`,
        })
      }
      seen.add(block.id)
    }
  })
export type BlockTemplateDocument = z.infer<typeof blockTemplateDocumentSchema>

/**
 * The HTML-first document (CLI-840): one email body as the author (or Claude, from the copyable
 * prompt) wrote it, with `{{tokens}}`, `data-cb-repeat` regions for headers and clips,
 * `data-cb-types` on each clips block naming the sources it renders, and `data-cb-slot` elements
 * where a report's own greeting, intro and sign-off land. The HTML owns the whole body: no
 * masthead, shell or blocks wrap it, because a template written as one document is one document.
 *
 * Email only. A Google Doc has its own page and typography, and an HTML fragment says nothing a
 * doc could use; a template that needs the doc target is a block document.
 */
export const htmlTemplateDocumentSchema = z.object({
  version: z.literal(2),
  kind: z.literal('html'),
  target: z.literal('email').default('email'),
  /**
   * The whole body. Ten times the block limit because this IS the template, not one block of it;
   * the same deny list the blocks carry applies, and the renderer's allowlist runs on top.
   */
  html: z
    .string()
    .max(200_000)
    .superRefine((html, ctx) => {
      for (const { pattern, message } of FORBIDDEN_HTML) {
        if (pattern.test(html)) ctx.addIssue({ code: 'custom', message })
      }
    }),
  /**
   * What a report put in the slots, set per render by `applyReportProse` and the global notice —
   * never by the editor, and never stored. Carried on the document because the renderer takes a
   * document, and the report's words are the one thing a render adds to the template's.
   */
  slots: z
    .object({
      greeting: z.string().max(5000).optional(),
      intro: z.string().max(5000).optional(),
      hideSignOff: z.boolean().optional(),
    })
    .optional(),
})
export type HtmlTemplateDocument = z.infer<typeof htmlTemplateDocumentSchema>

/**
 * Either kind. The HTML member is tried first: its `kind: 'html'` literal rejects a block document
 * at the first key, while a block document's refinements are the expensive part.
 */
export const templateDocumentSchema = z.union([
  htmlTemplateDocumentSchema,
  blockTemplateDocumentSchema,
])
export type TemplateDocument = z.infer<typeof templateDocumentSchema>

export function isHtmlTemplateDocument(doc: TemplateDocument): doc is HtmlTemplateDocument {
  return doc.kind === 'html'
}

export function isBlockTemplateDocument(doc: TemplateDocument): doc is BlockTemplateDocument {
  return doc.kind !== 'html'
}

/** Which shape a template is, as the list and the library badge say it. */
export const templateKindSchema = z.enum(['blocks', 'html'])
export type TemplateKind = z.infer<typeof templateKindSchema>

/**
 * The kind of a STORED document, read without parsing the whole thing — a list of summaries must
 * not pay for validating every account's documents. Anything that is not the HTML kind is a block
 * document, including the v1 shape, which `parseTemplateDocument` upgrades to one.
 */
export function templateKindOf(document: unknown): TemplateKind {
  return typeof document === 'object' &&
    document !== null &&
    (document as { kind?: unknown }).kind === 'html'
    ? 'html'
    : 'blocks'
}

/**
 * v1 carried brand on the document: `theme: { accent, font, masthead, showLogo }`. Brand moved to
 * the account, so v1 is read through this and written back as v2 — the translation step `version`
 * exists for. Leaning on Zod to strip the unknown keys would silently reinterpret a template's own
 * accent as the account's: a behaviour change disguised as a no-op.
 */
const templateDocumentV1Schema = z.object({
  version: z.literal(1),
  theme: z.object({
    accent: hexColorSchema,
    font: z.enum(['sans', 'serif']),
    masthead: z.string().max(200),
    showLogo: z.boolean(),
  }),
  blocks: z.array(templateBlockSchema).min(1).max(20),
})

/** Parses a stored document of any known version, upgrading it to the current shape. */
export function parseTemplateDocument(raw: unknown): TemplateDocument {
  const current = templateDocumentSchema.safeParse(raw)
  if (current.success) return current.data
  const v1 = templateDocumentV1Schema.parse(raw)
  return {
    version: 2,
    target: 'email',
    masthead: { text: v1.theme.masthead, showLogo: v1.theme.showLogo },
    blocks: v1.blocks,
  }
}

export function standardPresentation(): SectionPresentation {
  return {
    preset: 'standard',
    fields: { outlet: true, date: true, author: false, summary: false, readMore: false },
    snippets: { mode: 'matched', limit: 2, highlightTerms: true, style: 'block' },
    syndication: { show: true, limit: 10, sort: 'newest' },
    markers: { opinion: true, paywall: true },
    outletPlacement: 'meta',
    sort: 'ranked',
    articleLayout: 'blocks',
    lines: [],
    header: { show: true, rule: true },
    showEmptySections: false,
    sourceHeadings: defaultSourceHeadings(),
    bySource: {},
    // No deviations: the standard presentation is the renderer's own defaults, said out loud.
    styles: {},
    stylesBySource: {},
    articleHtml: {},
  }
}

/**
 * The renderer's neutral baseline: masthead, greeting, a short body, the sections, a signature,
 * all in the renderer's own defaults. The renderer's tests are written against it.
 *
 * NOT what a report without a template sends, and not what a new template starts as — both are
 * `standardTemplateDocument()` in starter-templates.ts, the legacy default as sent (CLI-793).
 */
export function defaultTemplateDocument(): BlockTemplateDocument {
  return {
    version: 2,
    target: 'email',
    masthead: { text: '{{report_title}}', showLogo: true },
    blocks: [
      {
        id: 'greeting',
        type: 'greeting',
        enabled: true,
        content:
          '<p>Good morning, <span data-merge-field="client_name">Client name</span> team,</p>',
      },
      {
        id: 'body',
        type: 'richText',
        enabled: true,
        content: '<p>Sharing today’s coverage — highlights below.</p>',
      },
      {
        id: 'sections',
        type: 'sections',
        enabled: true,
        default: standardPresentation(),
      },
      {
        id: 'signature',
        type: 'signature',
        enabled: true,
        source: 'custom',
        closing: 'Best,',
        content: '<p><span data-merge-field="sender_name">Sender name</span><br>Clipbook</p>',
      },
    ],
  }
}

/**
 * What a shared row IS to the system, beyond its name (CLI-838). `standard` is the one template
 * every report without a template renders through — the legacy portal's default, which used to be
 * code only and so could not be edited without a deploy. A role rather than a boolean so the next
 * built-in row is a value, not a column; `coverage_lists.role` is the precedent.
 */
export const templateRoleSchema = z.enum(['standard'])
export type TemplateRole = z.infer<typeof templateRoleSchema>
export const STANDARD_TEMPLATE_ROLE: TemplateRole = 'standard'

/**
 * A stored role as the wire knows it. A value this build has no name for reads as none rather
 * than failing the whole list — a row written by a newer deploy is still an ordinary template to
 * an older one, which is the right way round during a rollout.
 */
export function templateRoleOf(value: unknown): TemplateRole | null {
  const parsed = templateRoleSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}

/** Wire shape of one template. `document` rides whole — it's the thing the editor edits. */
export const reportTemplateSchema = z.object({
  id: z.string(),
  /**
   * Null means the shared library — a template that belongs to no account. Deliberately the only
   * signal: a separate `shared` boolean could disagree with it, and then two places would have to
   * be kept honest instead of one.
   */
  accountId: z.string().nullable(),
  name: z.string(),
  /** Null for every ordinary template; only a shared row carries one. */
  role: templateRoleSchema.nullable(),
  /** The document's shape, repeated here so the summary can say it without the document. */
  kind: templateKindSchema,
  document: templateDocumentSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type ReportTemplate = z.infer<typeof reportTemplateSchema>

/** List rows omit the document: the list needs names, not every account's full documents. */
export const reportTemplateSummarySchema = reportTemplateSchema.omit({ document: true })
export type ReportTemplateSummary = z.infer<typeof reportTemplateSummarySchema>

export const listReportTemplatesResponseSchema = z.object({
  templates: z.array(reportTemplateSummarySchema),
})
export type ListReportTemplatesResponse = z.infer<typeof listReportTemplatesResponseSchema>

/**
 * A row of the internal Template Library — the summary plus the owning account's NAME so the list
 * can render "Library / Acme" without an id lookup. Owner scope is still read off `accountId` (null =
 * the shared library), keeping a single signal per the note on reportTemplateSchema; `accountName` is
 * null exactly when `accountId` is.
 */
export const libraryTemplateSchema = reportTemplateSummarySchema.extend({
  accountName: z.string().nullable(),
})
export type LibraryTemplate = z.infer<typeof libraryTemplateSchema>

export const listAllTemplatesResponseSchema = z.object({
  templates: z.array(libraryTemplateSchema),
})
export type ListAllTemplatesResponse = z.infer<typeof listAllTemplatesResponseSchema>

/**
 * Copy a template into one or more scopes — each target an accountId, or `null` for the shared
 * library. Every target gets its OWN independent copy (name auto-suffixed on collision); this is a
 * clone, not a shared assignment, so there's no live link back to the source.
 */
export const copyTemplateSchema = z.object({
  targets: z.array(z.uuid().nullable()).min(1).max(50),
})
export type CopyTemplate = z.infer<typeof copyTemplateSchema>

export const copyTemplateResponseSchema = z.object({ copied: z.number().int().nonnegative() })
export type CopyTemplateResponse = z.infer<typeof copyTemplateResponseSchema>

/**
 * A template a report may choose, plus the one thing the choosing screen needs from inside it:
 * what an ordinary header will look like.
 *
 * Deliberately not the whole document — summaries omit it, and a picker has no use for the
 * greeting's HTML. But the report's Template tab seeds a per-header override from what that header
 * renders as today, and without this it would have to guess.
 */
/**
 * Which block of a template each override lands on, by block id — or null where the template has
 * no such block, which the Template tab shows as "this template has no greeting" instead of
 * offering an override that would change nothing.
 *
 * The intro is the first text block ABOVE the headers: that is where the legacy portal's one
 * message sat, and a designed template's text further down is part of its design, not a message.
 */
export function proseTargets(doc: TemplateDocument): {
  greeting: string | null
  intro: string | null
  signOff: string | null
} {
  // An HTML document has slots, not blocks: there is no block id to address. The report's words
  // reach it through `slots` (see `applyReportProse`), so the targets here are none.
  if (isHtmlTemplateDocument(doc)) return { greeting: null, intro: null, signOff: null }
  const sections = doc.blocks.findIndex((block) => block.type === 'sections')
  const above = sections === -1 ? doc.blocks : doc.blocks.slice(0, sections)
  return {
    greeting: doc.blocks.find((block) => block.type === 'greeting')?.id ?? null,
    intro: above.find((block) => block.type === 'richText')?.id ?? null,
    signOff: doc.blocks.find((block) => block.type === 'signature')?.id ?? null,
  }
}

/**
 * What the template itself says in each place a report can override (CLI-793), so the report's
 * Template tab can show "following the template: Good morning," instead of an unexplained blank.
 * Null where the template has no such block.
 */
export const templateProseSchema = z.object({
  greeting: z.string().nullable(),
  intro: z.string().nullable(),
  signOff: z.object({ closing: z.string(), content: z.string() }).nullable(),
})
export type TemplateProse = z.infer<typeof templateProseSchema>

/**
 * The inner HTML of a `data-cb-slot="<name>"` element, or null when the document has no such slot.
 *
 * A regex walk rather than a parser because this runs where no parser is (the schemas package, the
 * browser's picker). It counts the slot's own tag name in and out, so a slot holding elements of
 * its own kind — a translated signature is a `<div>` of two `<div>`s — still closes where it closes.
 */
/**
 * A sign-off slot as closing and content. A translated block signature is two `<div>`s — the
 * closing, then the name — and reads back as the two fields it came from, so the report's Template
 * tab still says "Best, / Sender name". Any other shape is all content, with no closing of its own.
 */
function htmlSignOff(slot: string): { closing: string; content: string } {
  const parts = /^\s*<div>([^<]*)<\/div>\s*<div>([\s\S]*)<\/div>\s*$/.exec(slot)
  if (parts === null) return { closing: '', content: slot }
  return { closing: parts[1] ?? '', content: parts[2] ?? '' }
}

export function htmlSlotContent(html: string, slot: string): string | null {
  const open = new RegExp(`<([a-z][a-z0-9]*)\\b[^>]*\\bdata-cb-slot="${slot}"[^>]*>`, 'i').exec(
    html,
  )
  if (!open) return null
  const tag = (open[1] ?? '').toLowerCase()
  const start = open.index + open[0].length
  const tags = new RegExp(`<(/?)${tag}\\b[^>]*>`, 'gi')
  tags.lastIndex = start
  let depth = 1
  for (let match = tags.exec(html); match !== null; match = tags.exec(html)) {
    depth += match[1] === '/' ? -1 : 1
    if (depth === 0) return html.slice(start, match.index)
  }
  return html.slice(start)
}

export function templateProseOf(doc: TemplateDocument): TemplateProse {
  if (isHtmlTemplateDocument(doc)) {
    const signOff = htmlSlotContent(doc.html, 'signoff')
    return {
      greeting: htmlSlotContent(doc.html, 'greeting'),
      intro: htmlSlotContent(doc.html, 'intro'),
      signOff: signOff === null ? null : htmlSignOff(signOff),
    }
  }
  const targets = proseTargets(doc)
  const byId = (id: string | null) =>
    id === null ? undefined : doc.blocks.find((b) => b.id === id)
  const greeting = byId(targets.greeting)
  const intro = byId(targets.intro)
  const signOff = byId(targets.signOff)
  return {
    greeting: greeting?.type === 'greeting' ? greeting.content : null,
    intro: intro?.type === 'richText' ? intro.content : null,
    signOff:
      signOff?.type === 'signature' ? { closing: signOff.closing, content: signOff.content } : null,
  }
}

export const selectableTemplateSchema = reportTemplateSummarySchema.extend({
  /** Null when the document has no sections block — nothing to inherit from. */
  defaultPresentation: sectionPresentationSchema.nullable(),
  /** Null when the stored document no longer parses — the tab then offers no prose overrides. */
  prose: templateProseSchema.nullable(),
})
export type SelectableTemplate = z.infer<typeof selectableTemplateSchema>

/**
 * The standard layout as the picker sees it: what a report with NO template renders through. From
 * the library's `standard` row where one is installed — `templateId` names it so the tab can link
 * to its editor — or the built-in document where none is, with `templateId` null. Sent alongside
 * the list rather than inside it: the standard is the `templateId = null` choice, and listing its
 * row as a second, pickable option would offer the same email twice (CLI-838).
 */
export const standardLayoutSchema = z.object({
  templateId: z.string().nullable(),
  name: z.string(),
  defaultPresentation: sectionPresentationSchema.nullable(),
  prose: templateProseSchema.nullable(),
})
export type StandardLayout = z.infer<typeof standardLayoutSchema>

export const listSelectableTemplatesResponseSchema = z.object({
  templates: z.array(selectableTemplateSchema),
  standard: standardLayoutSchema,
})
export type ListSelectableTemplatesResponse = z.infer<typeof listSelectableTemplatesResponseSchema>

/**
 * A test send from the template editor: the document as currently edited, filled with dummy
 * coverage in the shape of one real report's configuration, to a handful of addresses the sender
 * names (their own by default). Never the report's recipients — a test goes to who asked for it.
 */
export const templateTestSendSchema = z.object({
  reportId: z.uuid(),
  /** The template as named in the editor; it goes in the subject, so the inbox says which one. */
  templateName: z.string().trim().min(1).max(120),
  document: templateDocumentSchema,
  to: z.array(z.email()).min(1).max(5),
})
export type TemplateTestSend = z.infer<typeof templateTestSendSchema>

export const templateTestSendResponseSchema = z.object({
  to: z.array(z.string()),
  subject: z.string(),
})
export type TemplateTestSendResponse = z.infer<typeof templateTestSendResponseSchema>

/** Creation takes a name; the document is optional and defaults to the house format. */
export const createReportTemplateSchema = z.object({
  name: z.string().min(1).max(200),
  document: templateDocumentSchema.optional(),
})
export type CreateReportTemplate = z.infer<typeof createReportTemplateSchema>

/**
 * Duplicating a template. The name is optional because the common case is "give me a copy to edit"
 * and making someone name it first is a step between them and the thing they came to do — the
 * server picks a free one, and the editor is where they rename it.
 */
export const duplicateReportTemplateSchema = z.object({
  name: z.string().min(1).max(120).optional(),
})
export type DuplicateReportTemplateInput = z.infer<typeof duplicateReportTemplateSchema>

/**
 * The name a copy gets: "Thing (copy)", then "(copy 2)", "(copy 3)".
 *
 * Shared so both scopes number copies the same way, and so the rule is testable without a
 * database. Bounded rather than unbounded — a caller who has made 50 copies of one template has a
 * different problem, and a loop with no end is how a duplicate button hangs.
 */
export function freeCopyName(base: string, taken: readonly string[]): string {
  const used = new Set(taken)
  const first = `${base} (copy)`
  if (!used.has(first)) return first
  for (let n = 2; n <= 50; n++) {
    const candidate = `${base} (copy ${n})`
    if (!used.has(candidate)) return candidate
  }
  // Fifty copies of one template is a different problem. Returning the last candidate means the
  // insert fails on the unique index with "that name is taken", which is true and actionable —
  // better than inventing a timestamped name nobody asked for.
  return `${base} (copy 50)`
}

export const updateReportTemplateSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    document: templateDocumentSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: 'Nothing to update.' })
export type UpdateReportTemplate = z.infer<typeof updateReportTemplateSchema>

/**
 * The presentation as one source sees it: the header's, with that source's deviations laid over
 * it field by field.
 *
 * Field by field rather than wholesale, for the same reason the style resolver is: an override
 * that only changes the snippet limit must not freeze everything else at the values it happened
 * to be written with.
 */
export function presentationForSource(
  presentation: SectionPresentation,
  source: StyleSource | undefined,
): SectionPresentation {
  const override = source === undefined ? undefined : presentation.bySource[source]
  if (override === undefined) return presentation
  return {
    ...presentation,
    fields: withDefined(presentation.fields, override.fields),
    snippets: withDefined(presentation.snippets, override.snippets),
    syndication: withDefined(presentation.syndication, override.syndication),
    ...(override.lines !== undefined && { lines: override.lines }),
    ...(override.outletPlacement !== undefined && { outletPlacement: override.outletPlacement }),
  }
}

/**
 * Applies only the keys an override actually set.
 *
 * A plain spread would copy `undefined` for every key the override omitted, turning "says nothing
 * about the author" into "explicitly no author". Under `exactOptionalPropertyTypes` the compiler
 * catches it; without that it would have been a silent way to reset fields nobody touched.
 */
function withDefined<T extends object>(
  base: T,
  // `| undefined` on the values, not merely optional keys: with exactOptionalPropertyTypes a Zod
  // `.partial()` produces the former, and the two are different types.
  override: { [K in keyof T]?: T[K] | undefined } | undefined,
): T {
  if (override === undefined) return base
  const merged = { ...base }
  for (const [key, value] of Object.entries(override)) {
    if (value !== undefined) merged[key as keyof T] = value as T[keyof T]
  }
  return merged
}
